import { useMemo } from "react";
import { PIXEL_PER_TICK } from "../utils/timelineConstants";
import { formatTimeLabel, timeToX } from "../utils/timelineCalculations";
import type { VisibleXRange } from "./useVisibleXRange";

interface UseTimelineLabelsProps {
  scaledWidth: number;
  min: number;
  max: number;
  live?: boolean;
  /**
   * The visible slice of the scaled axis (see `useVisibleXRange`). Given one,
   * range mode only builds the labels inside it. Null renders the whole axis.
   */
  visibleRange?: VisibleXRange | null;
}

export interface TimelineTick {
  x: number;
  label: string;
  key?: number;
}

/** Round tick steps; live mode picks the smallest that yields ≤ ~8 ticks. */
const LIVE_TICK_STEPS_MS = [
  1_000, 5_000, 10_000, 15_000, 30_000,
  60_000, 120_000, 300_000, 600_000, 900_000, 1_800_000, 3_600_000,
];

export const useTimelineLabels = ({
  scaledWidth,
  min,
  max,
  live = false,
  visibleRange,
}: UseTimelineLabelsProps) => {
  const labels = useMemo<TimelineTick[]>(() => {
    const duration = max - min;

    if (duration <= 0 || !isFinite(duration)) {
      return [];
    }

    if (live) {
      const step =
        LIVE_TICK_STEPS_MS.find((s) => duration / s <= 8) ??
        LIVE_TICK_STEPS_MS[LIVE_TICK_STEPS_MS.length - 1];
      const first = Math.ceil(min / step) * step;
      const ticks: TimelineTick[] = [];
      for (let t = first; t <= max; t += step) {
        ticks.push({ x: timeToX(t, min, max, scaledWidth), label: formatTimeLabel(t), key: t });
      }
      return ticks;
    }

    const totalTicks = Math.floor(scaledWidth / PIXEL_PER_TICK);
    if (totalTicks <= 0) {
      return [];
    }

    /**
     * Build only what can be seen, plus a screen either side so scrolling never
     * reveals a gap before the next bucket lands.
     *
     * Zoom multiplies the axis width (`scaledWidth = width * zoom`, up to
     * MAX_ZOOM 500), so one tick per 100 px meant up to ~7,500 labels — every
     * one of them formatted, laid out and reconciled on every zoom step, while
     * the operator could see fifteen. That was the single most expensive thing
     * the timeline did.
     */
    const from = visibleRange ? visibleRange.from : 0;
    const to = visibleRange ? visibleRange.to : scaledWidth;
    const firstIndex = Math.max(0, Math.floor((from / scaledWidth) * totalTicks));
    const lastIndex = Math.min(totalTicks, Math.ceil((to / scaledWidth) * totalTicks));

    const ticks: TimelineTick[] = [];
    for (let i = firstIndex; i <= lastIndex; i++) {
      const rel = i / totalTicks;
      ticks.push({
        x: rel * scaledWidth,
        label: formatTimeLabel(min + rel * duration),
        // The GLOBAL tick index, not the position in this slice — scrolling
        // changes which ticks are built, and an index-in-slice key would make
        // React remount every label on every bucket change.
        key: i,
      });
    }
    return ticks;
  }, [scaledWidth, min, max, live, visibleRange]);

  return labels;
};
